import { z } from 'zod';
import type {
  ChatDateFilter,
  ChatCustomFilter,
  ChatFilterOperator,
  ChatSource,
} from '../enums/chat.filters.js';

export const DateModel = z.object({
  day: z.number(),
  month: z.number(),
  year: z.number(),
});
export type DateModelType = z.infer<typeof DateModel>;

export declare namespace PublishedDateFilter {
  export type Options =
    | 'all-time'
    | 'last-30-days'
    | 'last-90-days'
    | 'last-6-months'
    | 'last-12-months'
    | 'last-2-years'
    | 'last-5-years';

  export type Item = {
    id: Options;
    label: string;
  };
  export type Value = Date;
}

export declare namespace DataSourceFilter {
  export type Item = {
    id: string;
    name: string;
  };
}

export declare namespace RecordFilter {
  export type Item = {
    id: string;
    name: string;
    type: string;
  };
}

export declare namespace SourceFilter {
  export type Item = {
    id: ChatSource;
    name: string;
  };
}

export type Tag = {
  id: number;
  name: string;
  metadata?: {
    canRemove?: boolean;
    segmentIdentifier?: string | null;
  };
};

export declare namespace TagFilter {
  export type Item = Tag;
}

export declare namespace ProjectFilter {
  export type Item = {
    id: number;
    name: string;
  };
}

export declare namespace EngagementFilter {
  export type Item = {
    id: string;
    name: string;
  };
}

export declare namespace CustomFilter {
  type ItemValue = {
    id: string;
    name: string;
    parent1?: string;
    parent2?: string;
  };

  export type AgentCustomFilter = {
    operator: ChatFilterOperator[];
    type: ChatCustomFilter.AgentFilter;
    value: ItemValue[];
  };

  export type TherapeuticArea = {
    operator: ChatFilterOperator.Eq | ChatFilterOperator.Neq;
    type: ChatCustomFilter.TherapeuticArea;
    value: ItemValue[];
  };

  export type TherapeuticSubArea = {
    operator: ChatFilterOperator.Eq | ChatFilterOperator.Neq;
    type: ChatCustomFilter.TherapeuticSubArea;
    value: ItemValue[];
  };

  export type Product = {
    operator: ChatFilterOperator.Eq | ChatFilterOperator.Neq;
    type: ChatCustomFilter.Product;
    value: ItemValue[];
  };

  export type Source = {
    operator: ChatFilterOperator.Eq | ChatFilterOperator.Neq;
    type: ChatCustomFilter.Source;
    value: ItemValue[];
  };

  export type InsightType = {
    operator: ChatFilterOperator.Eq | ChatFilterOperator.Neq;
    type: ChatCustomFilter.InsightType;
    value: ItemValue[];
  };

  export type InterestArea = {
    operator: ChatFilterOperator.Eq | ChatFilterOperator.Neq;
    type: ChatCustomFilter.InterestArea;
    value: ItemValue[];
  };

  export type PracticeSetting = {
    operator: ChatFilterOperator.Eq | ChatFilterOperator.Neq;
    type: ChatCustomFilter.PracticeSetting;
    value: ItemValue[];
  };

  export type Sentiment = {
    operator: ChatFilterOperator.Eq | ChatFilterOperator.Neq;
    type: ChatCustomFilter.Sentiment;
    value: ItemValue[];
  };

  export type InteractionDate = {
    operator: ChatFilterOperator.MoreRecentThan | ChatFilterOperator.PriorTo;
    type: ChatCustomFilter.InteractionDate;
    value: ChatDateFilter;
  };

  export type InteractionStartEndDate = {
    operator: ChatFilterOperator.MoreRecentThan | ChatFilterOperator.PriorTo;
    type: ChatCustomFilter.InteractionStartEndDate;
    value: DateModelType;
  };

  export type InteractionType = {
    operator: ChatFilterOperator.Eq | ChatFilterOperator.Neq;
    type: ChatCustomFilter.InteractionType;
    value: ItemValue[];
  };

  export type InteractionSubType = {
    operator: ChatFilterOperator.Eq | ChatFilterOperator.Neq;
    type: ChatCustomFilter.InteractionSubType;
    value: ItemValue[];
  };

  export type InteractionDetail = {
    operator: ChatFilterOperator.Eq | ChatFilterOperator.Neq;
    type: ChatCustomFilter.InteractionDetail;
    value: ItemValue[];
  };

  export type CallCenterSource = {
    operator: ChatFilterOperator.Eq | ChatFilterOperator.Neq;
    type: ChatCustomFilter.CallCenterSource;
    value: ItemValue[];
  };

  export type CallCenterSubSource = {
    operator: ChatFilterOperator.Eq | ChatFilterOperator.Neq;
    type: ChatCustomFilter.CallCenterSubSource;
    value: ItemValue[];
  };

  export type SingleSelectFilter = ItemValue;

  export type MultiselectFilter = ItemValue[];

  type CustomFilterMap = {
    [ChatCustomFilter.AgentFilter]: AgentCustomFilter;
    [ChatCustomFilter.CallCenterSource]: CallCenterSource;
    [ChatCustomFilter.CallCenterSubSource]: CallCenterSubSource;
    [ChatCustomFilter.InteractionType]: InteractionType;
    [ChatCustomFilter.InteractionSubType]: InteractionSubType;
    [ChatCustomFilter.InteractionDetail]: InteractionDetail;
    [ChatCustomFilter.InsightType]: InsightType;
    [ChatCustomFilter.InteractionDate]: InteractionDate;
    [ChatCustomFilter.InteractionStartEndDate]: InteractionStartEndDate;
    [ChatCustomFilter.InterestArea]: InterestArea;
    [ChatCustomFilter.TherapeuticArea]: TherapeuticArea;
    [ChatCustomFilter.TherapeuticSubArea]: TherapeuticSubArea;
    [ChatCustomFilter.Product]: Product;
    [ChatCustomFilter.Source]: Source;
    [ChatCustomFilter.PracticeSetting]: PracticeSetting;
    [ChatCustomFilter.Sentiment]: Sentiment;
  };

  export type Item<T extends ChatCustomFilter = ChatCustomFilter, K = CustomFilterMap[T]> = K;
}

export type ChatFilterSearchResult = {
  id: string;
  name: string;
  parent1?: string;
  parent2?: string;
};

export type ChatFilters = {
  dataSources: DataSourceFilter.Item[];
  sources: SourceFilter.Item[];
  minDocumentDate: PublishedDateFilter.Value | null;
  projects: ProjectFilter.Item[];
  records: RecordFilter.Item[];
  tags: TagFilter.Item[];
  custom: CustomFilter.Item[];
  location: {
    latitude: number;
    longitude: number;
    address: string | null;
    kolId: number | null;
  };
  engagement: EngagementFilter.Item;
  arbitraryFields?: Record<string, unknown>;
};

export type ChatFilterOverrides = Partial<Pick<ChatFilters, 'records' | 'location'>>;
